import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { Security as SecurityIcon } from '@mui/icons-material';
import api from '../services/api';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

function AuditDashboard() {
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState(7); // days
  const [stats, setStats] = useState({
    totalActions: 0,
    actionsByType: [],
    actionsByDay: [],
    topUsers: [],
    recentAlerts: []
  });

  useEffect(() => {
    fetchStats();
  }, [timeRange]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      
      // Fetch audit statistics
      const [statsResponse, logsResponse] = await Promise.all([
        api.get('/audit/stats'),
        api.get('/audit/logs', {
          params: {
            startDate: new Date(Date.now() - timeRange * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            limit: 1000
          }
        })
      ]);

      const logs = logsResponse.data.logs;
      
      // Process statistics
      const actionCounts = {};
      const dailyCounts = {};
      const userActions = {};
      
      logs.forEach(log => {
        // Count by action type
        actionCounts[log.action] = (actionCounts[log.action] || 0) + 1;
        
        // Count by day
        const day = new Date(log.created_at).toLocaleDateString();
        dailyCounts[day] = (dailyCounts[day] || 0) + 1;
        
        // Count by user
        const userName = log.user_username || 'Unknown';
        userActions[userName] = (userActions[userName] || 0) + 1;
      });
      
      // Convert to arrays for charts
      const actionsByType = Object.entries(actionCounts)
        .map(([action, count]) => ({
          name: action.replace(/_/g, ' '),
          value: count
        }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 10);
      
      const actionsByDay = Object.entries(dailyCounts)
        .map(([date, count]) => ({
          date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          count
        }))
        .sort((a, b) => new Date(a.date) - new Date(b.date));
      
      const topUsers = Object.entries(userActions)
        .map(([user, count]) => ({
          user,
          actions: count
        }))
        .sort((a, b) => b.actions - a.actions)
        .slice(0, 5);
      
      // Identify recent critical actions
      const recentAlerts = logs
        .filter(log => 
          ['USER_DELETED', 'PASSWORD_RESET_BY_ADMIN', 'FAILED_LOGIN'].includes(log.action)
        )
        .slice(0, 5)
        .map(log => ({
          ...log,
          time: new Date(log.created_at).toLocaleString()
        }));
      
      setStats({
        totalActions: logs.length,
        actionsByType,
        actionsByDay,
        topUsers,
        recentAlerts
      });
      
    } catch (error) {
      console.error('Error fetching audit stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SecurityIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h4">Audit Dashboard</Typography>
        </Box>
        
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Time Range</InputLabel>
          <Select
            value={timeRange}
            label="Time Range"
            onChange={(e) => setTimeRange(e.target.value)}
          >
            <MenuItem value={1}>Last 24 Hours</MenuItem>
            <MenuItem value={7}>Last 7 Days</MenuItem>
            <MenuItem value={30}>Last 30 Days</MenuItem>
            <MenuItem value={90}>Last 90 Days</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={3}>
        {/* Summary Cards */}
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Actions
              </Typography>
              <Typography variant="h4">
                {stats.totalActions.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                in last {timeRange} days
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Actions Today
              </Typography>
              <Typography variant="h4">
                {stats.actionsByDay[stats.actionsByDay.length - 1]?.count || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Most Active User
              </Typography>
              <Typography variant="h6">
                {stats.topUsers[0]?.user || 'N/A'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {stats.topUsers[0]?.actions || 0} actions
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: '#fff3e0' }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Recent Alerts
              </Typography>
              <Typography variant="h4" color="warning.main">
                {stats.recentAlerts.length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Critical actions
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Activity Timeline */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Activity Timeline
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={stats.actionsByDay}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line 
                  type="monotone" 
                  dataKey="count" 
                  stroke="#8884d8" 
                  name="Actions"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Top Actions Pie Chart */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Top Actions
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={stats.actionsByType.slice(0, 5)}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => entry.name}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {stats.actionsByType.slice(0, 5).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Top Users */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Most Active Users
            </Typography>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={stats.topUsers}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="user" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="actions" fill="#82ca9d" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Recent Critical Actions */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Recent Critical Actions
            </Typography>
            <Box sx={{ maxHeight: 250, overflow: 'auto' }}>
              {stats.recentAlerts.map((alert, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 1.5,
                    mb: 1,
                    bgcolor: '#fff3e0',
                    borderRadius: 1,
                    borderLeft: '4px solid #ff9800'
                  }}
                >
                  <Typography variant="subtitle2">
                    {alert.action.replace(/_/g, ' ')}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    By: {alert.user_username || 'Unknown'} | {alert.time}
                  </Typography>
                </Box>
              ))}
              {stats.recentAlerts.length === 0 && (
                <Typography color="textSecondary">
                  No critical actions in this period
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default AuditDashboard;
